import {
  Dialog,
  IconButton,
  XCloseIcon,
  useMobileBreakpoint,
} from '@customink/pigment-react';
import type { DialogProps } from '@customink/pigment-react/lib/components/Dialog';
import type {
  Design,
  ProductColorId,
} from '@customink/project-service-react-components';
import { useConfigContext } from '@customink/project-service-react-components';

import Image from '@/components/Image';
import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';
import { proofBackground } from '@/utils/designTokens';

type ProductImageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  cid?: Design['cid'] | null;
  colorId?: ProductColorId;
  view?: string;
};

export function ProductImageModal({
  isOpen,
  onClose,
  cid,
  colorId,
  view = 'front_large_extended.png',
}: ProductImageModalProps) {
  const isMobile = useMobileBreakpoint();
  const url = useConfigContext().imagesUrl;

  const legacyOptions = '&pblegacy=1&pblegacysize=large&pblegacywm=1';
  const cidParams = cid ? `?design=${cid}${legacyOptions}` : '';
  const imageSrc = `${url}/colors/${colorId}/views/alt/${view}${cidParams}`;

  // Determine if the view is front or back based on the view parameter
  const viewType = view.toLowerCase().includes('front') ? 'front' : 'back';

  const handleClose: DialogProps['onClose'] = (_, reason) => {
    googleAnalyticsTag({
      event: 'cart_design_proof',
      interaction_type: 'close',
      design_proof_view: viewType,
    });

    onClose();
  };

  return (
    <Dialog
      data-testid="product-image-modal"
      hideCloseButton={true}
      onClose={handleClose}
      open={isOpen}
      padding={{
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
      }}
      scroll="content"
      title=""
      width="md"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          maxHeight: '80vh',
          position: 'relative',
        }}
      >
        <IconButton
          aria-label="close"
          color="secondary"
          data-testid="custom-close-button"
          fill={false}
          onClick={onClose}
          size="large"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <XCloseIcon htmlColor="grey" />
        </IconButton>
        <Image
          src={imageSrc}
          style={{
            width: '100%',
            backgroundColor: proofBackground,
            objectFit: 'contain',
            maxHeight: '100%',
          }}
        />
      </div>
    </Dialog>
  );
}
